"use client";

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield,
  Plus
} from 'lucide-react';
import AttachmentPreview from './AttachmentPreview';
import HSEAssessmentTab from './HSEAssessmentTab';
import DashboardTab from './health_safety/DashboardTab';
import IncidentFormModal from './health_safety/IncidentFormModal';
import IncidentsList from './health_safety/IncidentsList';
import IncidentDetailsModal from './health_safety/IncidentDetailsModal';
import AlertsTab from './health_safety/AlertsTab';


const HealthSafetyPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Incident reporting state
  const [incidentForm, setIncidentForm] = useState({
    title: '',
    description: '',
    incident_type: 'hazard',
    severity: 'low',
    location_description: '',
    latitude: null,
    longitude: null,
    witnesses: [],
    immediate_actions_taken: '',
    additional_notes: '',
    estimated_damage_cost: null
  });
  const [incidentImages, setIncidentImages] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);

  // Emergency alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertForm, setAlertForm] = useState({
    title: '',
    message: '',
    alert_type: 'general',
    priority: 'medium',
    acknowledgment_required: true
  });

  // Incidents list state
  const [incidents, setIncidents] = useState([]);
  const [incidentFilters, setIncidentFilters] = useState({
    status: '',
    type: '',
    severity: '',
    my_incidents: false
  });
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showIncidentDetailsModal, setShowIncidentDetailsModal] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  
  // State for incident form modal (for both create and edit)
  // const [showIncidentFormModal, setShowIncidentFormModal] = useState(false); // REMOVED: Redundant
  const [editingIncident, setEditingIncident] = useState(null); // Holds incident data if editing

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    // Load initial data based on active tab
    if (activeTab === 'incidents' || activeTab === 'dashboard') {
      loadIncidents();
    } else if (activeTab === 'alerts') {
      loadEmergencyAlerts();
    }
  }, [activeTab]);

  // Get current location for incident reporting
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        setCurrentLocation(location);
        setIncidentForm(prev => ({
          ...prev,
          latitude: location.latitude,
          longitude: location.longitude
        }));
        setIsLoading(false);
        setSuccess('Location captured successfully');
      },
      (error) => {
        setIsLoading(false);
        setError(`Location access denied: ${error.message}`);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };

  // Image upload for incidents handled locally within IncidentFormModal component

  // Function to close the incident form modal and reset state
  const closeIncidentFormModal = () => {
    setEditingIncident(null); // Clear editing state
    setIncidentForm({ // Reset form data
      title: '', description: '', incident_type: 'hazard', severity: 'low',
      location_description: '', latitude: null, longitude: null, witnesses: [],
      immediate_actions_taken: '', additional_notes: '', estimated_damage_cost: null
    });
    setIncidentImages([]); // Clear images
    setCurrentLocation(null); // Clear location
    setError(''); // Clear any errors
    setSuccess(''); // Clear any success messages
    setActiveTab('incidents'); // Crucial: switch back to incidents tab
  };

  // Submit incident report (create or update)
  const submitIncident = async () => {
    if (!incidentForm.title || !incidentForm.description || !incidentForm.location_description) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const incidentData = {
        ...incidentForm,
        // Send only the base64 data string for each image
        images: incidentImages.map(img => img.data),
        witnesses: incidentForm.witnesses.filter(w => w.trim() !== '')
      };

      let response;
      if (editingIncident) {
        // Update existing incident
        response = await fetch(`${BACKEND_URL}/api/health-safety/incidents/${editingIncident.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(incidentData)
        });
      } else {
        // Create new incident
        response = await fetch(`${BACKEND_URL}/api/health-safety/incidents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(incidentData)
        });
      }

      if (response.ok) {
        const result = await response.json();
        setSuccess(`Incident ${editingIncident ? 'updated' : 'reported'} successfully! ID: ${result.id}`);
        
        closeIncidentFormModal(); // Call the new function to close and reset
        
        // Reload incidents list
        loadIncidents();
      } else {
        const errorData = await response.json();
        if (Array.isArray(errorData.detail) && errorData.detail.length > 0 && errorData.detail[0].msg) {
          const validationErrors = errorData.detail.map(err => `${err.loc.join('.')} - ${err.msg}`).join('; ');
          setError(`Validation Error: ${validationErrors}`);
        } else {
          setError(errorData.detail || `Failed to ${editingIncident ? 'update' : 'submit'} incident report`);
        }
      }
    } catch (error) {
      setError(`Error ${editingIncident ? 'updating' : 'submitting'} incident: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Load incidents list
  const loadIncidents = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (incidentFilters.status) params.append('status_filter', incidentFilters.status);
      if (incidentFilters.type) params.append('incident_type', incidentFilters.type);
      if (incidentFilters.severity) params.append('severity', incidentFilters.severity);
      if (incidentFilters.my_incidents) params.append('my_incidents', 'true');
      
      const response = await fetch(`${BACKEND_URL}/api/health-safety/incidents?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setIncidents(data);
      }
    } catch (error) {
      console.error('Error loading incidents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load emergency alerts
  const loadEmergencyAlerts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/health-safety/alerts`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const processedAlerts = data.map(alert => ({
          ...alert,
          acknowledged_by: alert.acknowledged_by || []
        }));
        setAlerts(processedAlerts);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
      setAlerts([
        {
          id: 'alert-001',
          title: 'Fire Alarm Activated - Main Building',
          message: 'Fire alarm activated on 3rd floor of Main Building. Evacuate immediately. Follow emergency procedures.',
          alert_type: 'emergency',
          priority: 'critical',
          timestamp: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
          acknowledgment_required: true,
          acknowledged_by: []
        },
        {
          id: 'alert-002',
          title: 'Severe Weather Warning',
          message: 'Heavy thunderstorms expected in the area. Secure outdoor equipment and stay indoors.',
          alert_type: 'severe_weather',
          priority: 'high',
          timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          acknowledgment_required: false,
          acknowledged_by: []
        }
      ]);
    }
  };

  // Acknowledge alert
  const acknowledgeAlert = async (alertId, status = 'acknowledged') => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/health-safety/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          alert_id: alertId,
          user_id: user.id,
          location_latitude: currentLocation?.latitude,
          location_longitude: currentLocation?.longitude
        })
      });

      if (response.ok) {
        setSuccess('Alert acknowledged successfully');
        loadEmergencyAlerts();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to acknowledge alert');
      }
    } catch (error) {
      setError(`Error acknowledging alert: ${error.message}`);
    }
  };

  // Submit new alert (admin only)
  const submitAlert = async () => {
    if (!alertForm.title || !alertForm.message) {
      setError('Alert title and message are required');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/health-safety/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(alertForm)
      });
      if (response.ok) {
        setSuccess('Emergency alert sent successfully!');
        setAlertForm({ title: '', message: '', alert_type: 'general', priority: 'medium', acknowledgment_required: true });
        loadEmergencyAlerts();
      } else {
        const errorData = await response.json();
        let errorMessage = 'Failed to send alert';
        if (Array.isArray(errorData.detail) && errorData.detail.length > 0 && errorData.detail[0].msg) {
          errorMessage = `Validation Error: ${errorData.detail.map(err => `${err.loc.join('.')} - ${err.msg}`).join('; ')}`;
        } else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        setError(errorMessage);
      }
    } catch (err) {
      setError(`Error sending alert: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'investigating': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'reported': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper to get risk color
  const getRiskColor = (riskScore) => {
    if (riskScore >= 16) return 'bg-red-600 text-white';
    if (riskScore >= 9) return 'bg-orange-600 text-white';
    if (riskScore >= 4) return 'bg-yellow-600 text-white';
    return 'bg-green-600 text-white';
  };

  const handleDownloadAttachment = async (attachment) => {
    if (!selectedIncident) return;
    try {
        const response = await fetch(`${BACKEND_URL}/api/health-safety/incidents/${selectedIncident.id}/attachments/${attachment.id}`, {
            credentials: 'include'
        });
        if (!response.ok) {
            throw new Error('Download failed');
        }
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = attachment.filename || 'download';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
    } catch (err) {
        console.error('Download failed:', err);
        setError('Failed to download attachment.');
    }
  };

  // Render dashboard tab content — extracted to DashboardTab component

  // Render incident reporting form — extracted to IncidentFormModal component

  // Render incidents list — extracted to IncidentsList component
  const handleEditIncident = (incident) => {
    setEditingIncident(incident);
    setIncidentForm({
      title: incident.title || '',
      description: incident.description || '',
      incident_type: incident.incident_type || 'hazard',
      severity: incident.severity || 'low',
      location_description: incident.location_description || '',
      latitude: incident.latitude || null,
      longitude: incident.longitude || null,
      witnesses: incident.witnesses || [],
      immediate_actions_taken: incident.immediate_actions_taken || '',
      additional_notes: incident.additional_notes || '',
      estimated_damage_cost: incident.estimated_damage_cost || null
    });
    setIncidentImages(incident.images ? incident.images.map((img, idx) => {
      // Ensure existing images are loaded with 'data' property for resubmission
      // If img is already a structured object (from previous submission), use its data
      if (typeof img === 'object' && img.data) {
        return {
          id: img.id || `existing-${incident.id}-${idx}`,
          filename: img.filename || `image-${idx + 1}.png`,
          content_type: img.content_type || 'image/png',
          data: img.data
        };
      }
      // If img is just a base64 string (from initial load), create a structured object
      return {
        id: `existing-${incident.id}-${idx}`,
        filename: `image-${idx + 1}.png`, // Mock filename
        content_type: 'image/png', // Mock content type
        data: img // The base64 string
      };
    }) : []);
    setActiveTab('report'); // Change to the report tab for editing
  };

  // Render incident details modal — extracted to IncidentDetailsModal component

  // Render Emergency Alerts tab content — extracted to AlertsTab component

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
            <Shield className="mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6 text-red-500" />
            Health & Safety
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Report incidents, manage alerts, and access safety resources</p>
        </div>
        
        <button
          onClick={() => {
            setEditingIncident(null); // Ensure we're creating a new incident
            setIncidentForm({ // Reset form data for new incident
              title: '', description: '', incident_type: 'hazard', severity: 'low',
              location_description: '', latitude: null, longitude: null, witnesses: [],
              immediate_actions_taken: '', additional_notes: '', estimated_damage_cost: null
            });
            setIncidentImages([]);
            setCurrentLocation(null);
            setActiveTab('report'); // Change to the report tab
          }}
          className="bg-red-600 text-white px-4 py-3 sm:py-2 rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 w-full sm:w-auto text-sm sm:text-base font-medium"
        >
          <Plus size={16} />
          Report Incident
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        {/* Mobile Tab Dropdown */}
        <div className="sm:hidden mb-4">
          <label htmlFor="tab-select" className="sr-only">Select a tab</label>
          <select
            id="tab-select"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-base bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 font-medium"
          >
            <option value="dashboard">📊 Dashboard</option>
            <option value="incidents">📋 Incidents</option>
            <option value="report">➕ Report Incident</option>
            <option value="alerts">🚨 Emergency Alerts</option>
            <option value="hse-assessments">📋 HSE Assessments</option>
          </select>
        </div>

        {/* Desktop Tab Navigation */}
        <div className="hidden sm:flex space-x-4 md:space-x-8 overflow-x-auto">
          <button onClick={() => setActiveTab('dashboard')} className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-medium text-sm md:text-base whitespace-nowrap flex-shrink-0 ${activeTab === 'dashboard' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            Dashboard
          </button>
          <button onClick={() => setActiveTab('incidents')} className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-medium text-sm md:text-base whitespace-nowrap flex-shrink-0 ${activeTab === 'incidents' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            Incidents
          </button>
          <button onClick={() => setActiveTab('report')} className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-medium text-sm md:text-base whitespace-nowrap flex-shrink-0 ${activeTab === 'report' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            Report Incident
          </button>
          <button onClick={() => setActiveTab('alerts')} className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-medium text-sm md:text-base whitespace-nowrap flex-shrink-0 ${activeTab === 'alerts' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            Emergency Alerts
          </button>
          <button onClick={() => setActiveTab('hse-assessments')} className={`py-3 md:py-4 px-2 md:px-3 border-b-2 font-medium text-sm md:text-base whitespace-nowrap flex-shrink-0 ${activeTab === 'hse-assessments' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            HSE Assessments
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-8">
        {activeTab === 'dashboard' && (
          <DashboardTab
            incidents={incidents}
            isLoading={isLoading}
          />
        )}
        {activeTab === 'incidents' && (
          <IncidentsList
            incidents={incidents}
            incidentFilters={incidentFilters}
            setIncidentFilters={setIncidentFilters}
            onLoadIncidents={loadIncidents}
            onViewIncident={(incident) => {
              setSelectedIncident(incident);
              setShowIncidentDetailsModal(true);
            }}
            onEditIncident={handleEditIncident}
            getSeverityColor={getSeverityColor}
            getStatusColor={getStatusColor}
            getRiskColor={getRiskColor}
          />
        )}
        {activeTab === 'report' && (
          <IncidentFormModal
            editingIncident={editingIncident}
            incidentForm={incidentForm}
            setIncidentForm={setIncidentForm}
            incidentImages={incidentImages}
            setIncidentImages={setIncidentImages}
            currentLocation={currentLocation}
            isLoading={isLoading}
            error={error}
            success={success}
            onClose={closeIncidentFormModal}
            onSubmit={submitIncident}
            onGetLocation={getCurrentLocation}
          />
        )}
        {activeTab === 'alerts' && (
          <AlertsTab
            alerts={alerts}
            alertForm={alertForm}
            setAlertForm={setAlertForm}
            onSubmitAlert={submitAlert}
            onAcknowledgeAlert={acknowledgeAlert}
            isLoading={isLoading}
            error={error}
            success={success}
            user={user}
            getSeverityColor={getSeverityColor}
          />
        )}
        {activeTab === 'hse-assessments' && <HSEAssessmentTab />}
      </div>

      {/* Modals */}
      {showIncidentDetailsModal && (
        <IncidentDetailsModal
          selectedIncident={selectedIncident}
          onClose={() => {
            setSelectedIncident(null);
            setShowIncidentDetailsModal(false);
          }}
          onDownloadAttachment={handleDownloadAttachment}
          onPreviewAttachment={setPreviewAttachment}
          getSeverityColor={getSeverityColor}
          getStatusColor={getStatusColor}
          getRiskColor={getRiskColor}
        />
      )}
      {previewAttachment && (
        <AttachmentPreview
          attachments={previewAttachment.attachments}
          initialIndex={previewAttachment.index}
          onClose={() => setPreviewAttachment(null)}
          incidentId={selectedIncident?.id}
        />
      )}
    </div>
  );
};

export default HealthSafetyPage;